import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import classNames from 'classnames';
import { useHistory } from 'react-router-dom';
import { MemoryHistory } from 'history';

import {
  showFullScreenPlayer,
  exitFullScreenPlayer,
  setHeroIndex,
} from 'actions/hero';

import { useRestoration } from 'hooks/restoration';
import { useAuthN } from 'hooks/auth/subscriptions';
import useTracking from 'hooks/analytics';

import CarouselSlide from 'components/pages/home/carousel/CarouselItem';
import CarouselDots from 'components/pages/home/carousel/CarouselDots';
import FocusGroup from 'components/focus/FocusGroup';
import device from 'common/support/device';
import appActions from 'common/support/applicationActions';
import focusManager from 'support/focus/FocusManager';
import { getCarouselMetadata } from 'support/analytics/analyticsHelper';
import { VideoCtvRedux, KitcenSinkRowItemDataItem, RelatedItem } from 'store-types';
import styles from './Carousel.module.scss';

const TRANSITION_TIME = 1000;
const DEFAULT_INTERVAL = 4000;

interface CarouselProps {
  id?: string;
  data?: (KitcenSinkRowItemDataItem | RelatedItem | null)[] | null;
  enableAutoScroll?: boolean;
  carouselSlideClassname?: string | null;
  onFocusIndexChanged?: () => void;
  onFocusDelegated?: () => void;
  onFocusSetOverride?: ((val: boolean) => void) | null;
  restorationId?: string | null;
  hideCarousel?: boolean;
  shouldCleanUp?: boolean;
  shouldAnimate?: boolean;
  onError?: ((error: Error) => void) | null;
  carouselFocused?: boolean;
  onUpcomingEventClick?: ((videoId: string) => void) | null;
}

const trackCarouselEvent = (
  trackEvent: (eventName: string, eventData: Record<string, unknown>) => void,
  { slideNum, action, title }: { slideNum: number; action: string; title?: string },
) => {
  const eventData = {
    ...getCarouselMetadata(true, slideNum),
    moduleAction: action,
    moduleCampaign: 'hero carousel interactions',
    clickText: `hero carousel - ${title}`,
  };
  trackEvent('carousel interaction', eventData);
};

const Carousel = ({
  id,
  data = null,
  enableAutoScroll = false,
  carouselSlideClassname = '',
  onFocusIndexChanged = () => {},
  onFocusDelegated = () => {},
  onFocusSetOverride = () => {},
  restorationId = null,
  hideCarousel = false,
  shouldAnimate = true,
  onError = () => {},
  carouselFocused = false,
  onUpcomingEventClick = () => {},
}: CarouselProps) => {
  const dispatch = useDispatch();
  const history = useHistory() as MemoryHistory;
  const { trackEvent } = useTracking();

  const [currentIndex, setCurrentIndex] = useState<string|number|undefined>(0);
  const [transitioning, setTransitioning] = useState(false);
  const [disableNextTransition, setDisableNextTransition] = useState(false);
  const [useCSSTransitions, setUseCssTransitions] = useState(true);
  const [rotationComplete, setRotationComplete] = useState(false);

  const auth = useSelector((state: VideoCtvRedux) => state.auth);

  const fullScreenPlayer = useSelector((state: VideoCtvRedux) => state.hero.fullScreenPlayer);
  const useConfigValue = useSelector(
    (state: VideoCtvRedux) => state.kitchenSink.data?.config?.modules?.timeouts?.enabled,
  );
  const ksRotationInterval = useSelector(
    (state: VideoCtvRedux) =>
      state.kitchenSink.data?.config?.modules?.timeouts?.params?.carouselSpeed,
  );
  const manualOverrideInterval = useSelector(
    (state: VideoCtvRedux) =>
      state.kitchenSink.data?.config?.modules?.timeouts?.params
        ?.carouselUserTimeout,
  );
  const setIndex = useSelector((state: VideoCtvRedux) => state.hero.setIndex);

  useEffect(() => {
    if (setIndex !== null) {
      setCurrentIndex(setIndex);
      dispatch(setHeroIndex(null)); // reset back to null
    }
  }, [setIndex, dispatch]);

  const rotationInterval = useConfigValue
    ? (ksRotationInterval as unknown as number) * 1000
    : DEFAULT_INTERVAL;
  const autoScrollTime = TRANSITION_TIME + rotationInterval; // Incrementing `transitionTime` ensures auto scroll wont fire before transition finishes
  const [transitionCSSTransform, setTransitionCSSTransform] = useState(
    `transform ${TRANSITION_TIME}ms`,
  );
  const manualOverrideTime = useConfigValue
    ? (manualOverrideInterval as unknown as number) * 1000
    : DEFAULT_INTERVAL;

  /**
   * Set whether to use CSS transitions.
   */
  useEffect(() => {
    const { useCssTransitions } = device;

    if (typeof useCssTransitions !== 'undefined') {
      // Use to check whether to enable css transitions
      setUseCssTransitions(useCssTransitions);
      if (!useCssTransitions) setTransitionCSSTransform(''); // Remove style
    }
  }, [setUseCssTransitions]);

  const autoScrollIntervalRef = useRef<ReturnType<typeof setInterval>|number|undefined>();
  const timeoutRef = useRef<ReturnType<typeof setTimeout>|number|undefined>();

  const { restorationState } = useRestoration(
    restorationId,
  );

  useEffect(() => {
    if (restorationState) {
      setCurrentIndex(restorationState?.currentIndex);
      autoScrollIntervalRef.current = restorationState?.intervalId;
      timeoutRef.current = restorationState?.timeoutId;
    } else {
      setCurrentIndex(0);
    }
  }, [restorationState]);

  const onTileFocused = useCallback(
    (focusIndex: number) => {
      if (currentIndex !== focusIndex) {
        setTransitioning(useCSSTransitions);
        setCurrentIndex(focusIndex);
      }
    },
    [currentIndex, useCSSTransitions],
  );

  const focusTile = useCallback(
    (index: number) => {
      const focusedCtaId = `${id}-${index}`;
      focusManager.setCurrentFocus(focusedCtaId);
    },
    [id],
  );

  const scrollToNextTile = useCallback(
    (action?: string | null, autoScroll = true, isFullScreenPlayer?: boolean) => {
      let newIndex = (currentIndex as number) + 1;

      if (action === appActions.moveLeft) {
        newIndex = (currentIndex as number) - 1;
        if (newIndex < 0 && !isFullScreenPlayer) {
          focusManager.setCurrentFocus('left-nav-focusable-content');
          return;
        }
      } else if (newIndex >= (data?.length as number)) {
        newIndex = 0;
        setDisableNextTransition(true);
      }

      trackCarouselEvent(trackEvent, {
        slideNum: newIndex + 1,
        action: autoScroll ? 'impression-auto' : 'impression-swipe',
        title: data?.[newIndex]?.about?.title,
      });

      const currentFocusId = focusManager.currentFocusId !== null && focusManager.currentFocusId ? `${focusManager.currentFocusId}` : '';
      const isFocusedOnCarousel = currentFocusId.includes(
        'home-carousel',
      );
      if (isFocusedOnCarousel) {
        focusTile(newIndex);
      }
      if ((currentIndex as number) + 1 >= (data?.length as number) && autoScroll) {
        setRotationComplete(true);
      }
    },
    [currentIndex, data, focusTile, trackEvent],
  );

  const savedCallback = useRef<typeof scrollToNextTile>();
  useEffect(() => {
    savedCallback.current = scrollToNextTile;
  });

  const isFirstItemPlayable = useAuthN(data?.[0]?.authTypes);

  const autoPlayVideo = useCallback(() => {
    const firstItemVideoId = data?.[0]?.id;
    if (isFirstItemPlayable && firstItemVideoId) {
      dispatch(showFullScreenPlayer(firstItemVideoId));
    } else {
      // play autoPlay object
      history.push({
        pathname: '/playback',
      });
    }
    trackCarouselEvent(trackEvent, {
      slideNum: 1,
      action: 'autoplay',
      title: data?.[0]?.about?.title,
    });
    setRotationComplete(false);
  }, [isFirstItemPlayable, dispatch, data, history, trackEvent]);

  const scrollToNext = useCallback((scrollAction = null) => {
    savedCallback.current?.(scrollAction);
  }, []);

  const addAutoScrollTimer = useCallback(() => {
    autoScrollIntervalRef.current = setInterval(scrollToNext, autoScrollTime);
  }, [autoScrollTime, scrollToNext]);

  const addManualTimer = useCallback(() => {
    timeoutRef.current = setTimeout(() => {
      scrollToNext();
      addAutoScrollTimer();
    }, manualOverrideTime);
  }, [addAutoScrollTimer, manualOverrideTime, scrollToNext]);

  const addIdleTimer = useCallback(() => {
    timeoutRef.current = setTimeout(autoPlayVideo, autoScrollTime);
  }, [autoPlayVideo, autoScrollTime]);

  const clearAutoScrollTimer = useCallback(() => {
    clearInterval(autoScrollIntervalRef.current);
    clearTimeout(timeoutRef.current);
  }, []);

  const firstTimeInFocus = useRef(true);
  useEffect(() => {
    if (data?.length && enableAutoScroll && carouselFocused) {
      if (!autoScrollIntervalRef.current && firstTimeInFocus.current) {
        addAutoScrollTimer();
        firstTimeInFocus.current = false;
      } else if (rotationComplete) {
        clearAutoScrollTimer();
        addIdleTimer();
      } else {
        clearAutoScrollTimer();
        addManualTimer();
      }
    }
    return () => {
      clearAutoScrollTimer();
    };
  }, [
    data,
    enableAutoScroll,
    autoScrollTime,
    carouselFocused,
    manualOverrideTime,
    rotationComplete,
    addAutoScrollTimer,
    scrollToNext,
    addManualTimer,
    clearAutoScrollTimer,
    addIdleTimer,
  ]);

  // Handle when carousel has rotated through all items
  useEffect(() => {
    if (rotationComplete) {
      clearAutoScrollTimer();
      addIdleTimer();
    }
  }, [
    rotationComplete,
    autoPlayVideo,
    autoScrollTime,
    clearAutoScrollTimer,
    addIdleTimer,
  ]);

  const [upcomingEvent, setUpcomingEvent] = useState(false);
  useEffect(() => {
    const eventStatus = typeof currentIndex !== 'undefined' ? data?.[currentIndex as number]?.event?.status : undefined;
    if (eventStatus === 'upcoming') {
      setUpcomingEvent(true);
    } else {
      setUpcomingEvent(false);
    }
  }, [currentIndex, data]);

  const onFocusSetOverrideFunction = useCallback(
    (val: boolean) => {
      onFocusSetOverride?.(val);
      return (currentIndex as number);
    },
    [currentIndex, onFocusSetOverride],
  );

  const onFocusIndexOverride = (_action: string, _focusIndex: number) => {
    if (transitioning) {
      return 'handled' as const;
    }

    return false as const;
  };

  const viewWidth = 1920;

  const showCarouselDots = !fullScreenPlayer && (data?.length ?? 0) > 1;

  const handleError = (error: unknown) => {
    if (!fullScreenPlayer) return;
    onError?.(error as Error);
  };

  const onAction = (action: string) => {
    if (hideCarousel || action === appActions.moveDown) {
      clearAutoScrollTimer();
      return;
    }

    if (action === appActions.moveLeft || action === appActions.moveRight) {
      const autoScroll = false;
      scrollToNextTile(action, autoScroll, fullScreenPlayer);
    }

    clearAutoScrollTimer();
    addManualTimer();
  };

  const navigateToPlayer = useCallback(
    (rowId: string | null, videoId: string | null) => {
      const params = new URLSearchParams();
      if (videoId) params.set('video', videoId);
      if (rowId) params.set('row', rowId);
      const search = params.toString();

      dispatch(exitFullScreenPlayer());

      history.push({
        pathname: '/playback',
        ...(search ? { search } : {}),
      });
    },
    [history, dispatch],
  );

  const navigateToHeroPlayer = useCallback(
    (rowId?: string | null, videoId?: string | null, video?: KitcenSinkRowItemDataItem | RelatedItem | null) => {
      const params = new URLSearchParams();
      if (videoId) params.set('video', videoId);
      if (rowId) params.set('row', rowId);
      const search = params.toString();

      if (
        auth.paramountPlus?.user?.data?.userPackage ===
          'CBS_ALL_ACCESS_LOW_COST_PACKAGE' &&
        (auth.adobe?.authN == null || !video?.authTypes?.includes('adobe')) &&
        !video?.authenticationDetails?.paramountplus?.essentials &&
        video?.authenticationDetails?.paramountplus?.premium
      ) {
        // user auth is p+ essentials
        // user is either not logged in with mvpd or (if they are logged in with mvpd) the stream is not mvpd authorized
        // content is p+ premium only
        // show upgrade to watch screen
        history.push({
          pathname: '/upgradeToWatch',
          ...(search ? { search } : {}),
        });
      } else {
        dispatch(showFullScreenPlayer(videoId));

        history.push({
          pathname: '/hero',
          ...(search ? { search } : {}),
        });
      }
    },
    [history, dispatch, auth],
  );

  const handleOnClick = (videoId?: string, isPlayable?: boolean, video?: KitcenSinkRowItemDataItem | RelatedItem | null) => {
    clearAutoScrollTimer();

    if (upcomingEvent) {
      onUpcomingEventClick?.(videoId ?? '');
    } else {
      trackCarouselEvent(trackEvent, {
        slideNum: (currentIndex as number) + 1,
        action: 'click',
        title: data?.[currentIndex as number]?.about?.title,
      });

      if (isPlayable) {
        navigateToHeroPlayer(null, videoId ?? '', video);
      } else {
        navigateToPlayer(null, videoId ?? '');
      }
    }
  };

  const handlePostGame = (videoId: string) => {
    navigateToPlayer(null, videoId);
  };

  return (
    <div
      className={classNames(
        'overflow-hidden',
        'position-fixed',
        'w-100',
        fullScreenPlayer && 'h-100',
        styles.carousel,
        {
          [styles.animate]: !device.isSlowDevice && shouldAnimate,
          [styles.showCarousel]: !hideCarousel,
          [styles.hideCarousel]: hideCarousel,
        },
      )}
    >
      {!!data?.length && (
        <>
          <FocusGroup
            id={id}
            focusId={id}
            style={{
              transition: disableNextTransition
                ? 'transform 1ms'
                : transitionCSSTransform,
              // replace vw by px to fix transition animation not showing up on 2016 samsung tizen
              transform: `translateX(-${(currentIndex as number) * viewWidth}px)`,
              WebkitTransform: `translateX(-${(currentIndex as number) * viewWidth}px)`,
            }}
            className={classNames(
              styles.carouselViewport,
              'h-100',
              'w-100',
              'carousel-focus-group',
            )}
            onAction={onAction}
            onFocusIndexChanged={onFocusIndexChanged}
            onFocusDelegated={onFocusDelegated}
            onFocusSetOverride={onFocusSetOverrideFunction}
            onFocusIndexOverride={onFocusIndexOverride}
            onTransitionEnd={() => {
              setDisableNextTransition(false);
              setTransitioning(false);
            }}
            horizontal
          >
            {data?.map((video, index) => {
              const slideId = `${id}-${index}`;
              return (
                <CarouselSlide
                  className={classNames(
                    'carousel-slide',
                    carouselSlideClassname,
                  )}
                  key={slideId}
                  focusId={slideId}
                  onTileFocused={() => {
                    onTileFocused(index);
                  }}
                  hideCarousel={hideCarousel}
                  onError={handleError}
                  onClick={handleOnClick}
                  handlePostGame={handlePostGame}
                  video={video}
                  slideNum={index + 1}
                  totalNum={data?.length}
                />
              );
            })}
          </FocusGroup>
          {showCarouselDots && (
            <div
              className={classNames(
                'position-absolute d-flex justify-content-center',
                styles.carouselDots,
              )}
            >
              <CarouselDots
                currentIndex={currentIndex as number | undefined}
                numDots={data?.length}
              />
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default Carousel;
